import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Typography, Paper, Grid, Box, Button } from '@mui/material';
import { theme as dasTheme, Link } from 'daaas-ui';
import Icon from '@mdi/react';
import {
  mdiFinance,
  mdiAccountGroup,
  mdiServerSecurity,
  mdiTransitDetour,
  mdiChevronRight,
} from '@mdi/js';

import Banner from '../components/UI/Banner';
import ContentContainer from '../components/Layout/ContentContainer';
import ContentSection from '../components/Layout/ContentSection';
import BrandingDAS from '../components/Layout/BrandingDAS';

const Root = styled('div')(({ theme }) => ({
  '.banner__header': {
    fontSize: `6rem !important`,
    lineHeight: '0.9375',
    letterSpacing: '0.47',
    [theme.breakpoints.down('sm')]: {
      fontSize: `5rem !important`,
    },
  },
  '& .about__cardIcon': {
    textAlign: 'center',
  },
  '& .about__card': {
    height: '100%',
    boxSizing: 'border-box',
  },
  '& .about__blueCard': {
    border: '2px solid',
    borderColor: theme.palette.primary.main,
    '& svg': {
      color: theme.palette.primary.main,
    },
  },
  '& .about__orangeCard': {
    border: '2px solid',
    borderColor: theme.palette.secondary.main,
    '& svg': {
      color: theme.palette.secondary.main,
    },
  },
  '& .about__cardList': {
    paddingLeft: theme.spacing(2),
  },
  '& .about__table': {
    width: '100%',
    borderCollapse: 'collapse',
    '& th': {
      textAlign: 'left',
    },
    '& th, td': {
      padding: theme.spacing(2),
    },
    '& tbody>tr': {
      borderTop: '2px solid',
      borderTopColor: theme.palette.primary.main,
    },
    '& tbody th': {
      width: '20%',
    },
  },
  '& .about__learnMoreLinks': {
    paddingLeft: theme.spacing(2),
  },

  '& .about__getStartedButton': {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '350px',
    },
    height: '3.5rem !important',
  },
}));

export default function AboutPage() {
  const { t, i18n } = useTranslation(['about', 'common']);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const isSmScreen = windowWidth < dasTheme.breakpoints.values.md;

  React.useEffect(() => {
    setTimeout(() => {
      document.title = `${t('common:daaas')} - ${t('common:about')}`;
      document
        .querySelector("meta[name='description']")
        .setAttribute('content', t('about:description'));
      document
        .querySelector("meta[name='keywords']")
        .setAttribute('content', t('about:keywords'));
      document
        .querySelector("link[rel='canonical']")
        .setAttribute('href', t('about:canonical'));
      document
        .querySelector("meta[name='dcterms.title']")
        .setAttribute('content', t('about:dctermsTitle'));
      document
        .querySelector("meta[name='dcterms.description']")
        .setAttribute('content', t('about:dctermsDescription'));
      document
        .querySelector("meta[name='dcterms.creator']")
        .setAttribute('content', t('common:dctermsCreator'));
      document
        .querySelector("meta[name='dcterms.language']")
        .setAttribute('content', t('common:dctermsLanguage'));
      document
        .querySelector("meta[name='dcterms.subject']")
        .setAttribute('content', t('common:dctermsSubject'));
    }, '100');
  }, [i18n.language]);

  React.useEffect(() => {
    // Detect screen size
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    window.addEventListener('ScreenOrientation', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('ScreenOrientation', handleResize);
    };
  }, []);

  return (
    <Root>
      <Banner>
        <Typography
          className="banner__header"
          variant="h1"
          color="textPrimary"
          sx={{ my: 10 }}
        >
          {t('common:about')}
          <br />
          <abbr title={t('common:daaas')}>{t('common:daaasAcronym')}</abbr>
        </Typography>
      </Banner>
      <ContentContainer paddingBottom={false}>
        <ContentSection heading={t('section1Heading')}>
          <Typography sx={{ mb: 2 }}>{t('section1P1')}</Typography>
          <Typography sx={{ mb: 2 }}>{t('section1P2')}</Typography>
          <Typography sx={{ mb: 4 }}>{t('section1P3')}</Typography>
        </ContentSection>
        <ContentSection heading={t('section2Heading')}>
          <Grid container sx={{ pt: 2 }} spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                className="das-paper about__card about__blueCard"
                sx={{ p: 2 }}
              >
                <Typography variant="h3">{t('efficiency')}</Typography>
                <Box className="about__cardIcon">
                  <Icon path={mdiFinance} size={5} />
                </Box>
                <ul className="about__cardList">
                  <li>
                    <Typography>{t('efficiencyList1')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('efficiencyList2')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('efficiencyList3')}</Typography>
                  </li>
                </ul>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                className="das-paper about__card about__orangeCard"
                sx={{ p: 2 }}
              >
                <Typography variant="h3">{t('flexibility')}</Typography>
                <Box className="about__cardIcon">
                  <Icon path={mdiTransitDetour} size={5} />
                </Box>
                <ul className="about__cardList">
                  <li>
                    <Typography>{t('flexibilityList1')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('flexibilityList2')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('flexibilityList3')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('flexibilityList4')}</Typography>
                  </li>
                </ul>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                className="das-paper about__card about__orangeCard"
                sx={{ p: 2 }}
              >
                <Typography variant="h3">{t('collaboration')}</Typography>
                <Box className="about__cardIcon">
                  <Icon path={mdiAccountGroup} size={5} />
                </Box>
                <ul className="about__cardList">
                  <li>
                    <Typography>{t('collaborationList1')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('collaborationList2')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('collaborationList3')}</Typography>
                  </li>
                </ul>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                className="das-paper about__card about__blueCard"
                sx={{ p: 2 }}
              >
                <Typography variant="h3">{t('security')}</Typography>
                <Box className="about__cardIcon">
                  <Icon path={mdiServerSecurity} size={5} />
                </Box>
                <ul className="about__cardList">
                  <li>
                    <Typography>{t('securityList1')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('securityList2')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('securityList3')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('securityList4')}</Typography>
                  </li>
                </ul>
              </Paper>
            </Grid>
          </Grid>
        </ContentSection>
        <ContentSection heading={t('section3Heading')}>
          <Typography sx={{ mb: 2 }}>{t('section3P')}</Typography>
          {isSmScreen ? (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Paper className="das-paper" sx={{ p: 2 }}>
                  <Link
                    href={`${t('common:/urlPrefix')}${t('common:/cae')}`}
                    target="_blank"
                  >
                    <Typography variant="h3" sx={{ mb: 4 }}>
                      {t('common:cae')}
                    </Typography>
                  </Link>
                  <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
                    {t('tableRowHead1')}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>{t('tableWhatCae')}</Typography>
                  <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
                    {t('tableRowHead2')}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>{t('tableWhoCae')}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper className="das-paper" sx={{ p: 2 }}>
                  <Link
                    href={`${t('common:/urlPrefix')}${t('common:/aaw')}`}
                    target="_blank"
                  >
                    <Typography variant="h3" sx={{ mb: 4 }}>
                      {t('common:aaw')}
                    </Typography>
                  </Link>
                  <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
                    {t('tableRowHead1')}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>{t('tableWhatAaw')}</Typography>
                  <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
                    {t('tableRowHead2')}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>{t('tableWhoAaw')}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper className="das-paper" sx={{ p: 2 }}>
                  <Link
                    href={`${t('common:/urlPrefix')}${t('common:/gae')}`}
                    target="_blank"
                  >
                    <Typography variant="h3" sx={{ mb: 4 }}>
                      {t('common:gae')}
                    </Typography>
                  </Link>
                  <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
                    {t('tableRowHead1')}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>{t('tableWhatGae')}</Typography>
                  <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
                    {t('tableRowHead2')}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>{t('tableWhoGae')}</Typography>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Paper className="das-paper about__blueCard">
              <table className="about__table">
                <thead>
                  <tr>
                    <td />
                    <th scope="col">
                      <Link
                        href={`${t('common:/urlPrefix')}${t('common:/cae')}`}
                      >
                        <Typography variant="h3" component="span">
                          {t('common:cae')}
                        </Typography>
                      </Link>
                    </th>
                    <th scope="col">
                      <Link
                        href={`${t('common:/urlPrefix')}${t('common:/aaw')}`}
                      >
                        <Typography variant="h3" component="span">
                          {t('common:aaw')}
                        </Typography>
                      </Link>
                    </th>
                    <th scope="col">
                      <Link
                        href={`${t('common:/urlPrefix')}${t('common:/gae')}`}
                      >
                        <Typography variant="h3" component="span">
                          {t('common:gae')}
                        </Typography>
                      </Link>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">
                      <Typography variant="h3" component="span" color="primary">
                        {t('tableRowHead1')}
                      </Typography>
                    </th>
                    <td>
                      <Typography>{t('tableWhatCae')}</Typography>
                    </td>
                    <td>
                      <Typography>{t('tableWhatAaw')}</Typography>
                    </td>
                    <td>
                      <Typography>{t('tableWhatGae')}</Typography>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <Typography variant="h3" component="span" color="primary">
                        {t('tableRowHead2')}
                      </Typography>
                    </th>
                    <td>
                      <Typography>{t('tableWhoCae')}</Typography>
                    </td>
                    <td>
                      <Typography>{t('tableWhoAaw')}</Typography>
                    </td>
                    <td>
                      <Typography>{t('tableWhoGae')}</Typography>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Paper>
          )}
        </ContentSection>
        {/* Comment out Learn More section until pages are ready */}
        <ContentSection heading={t('section4Heading')}>
          <ul className="about__learnMoreLinks">
            <li>
              <Typography sx={{ mb: 2 }}>
                <Link href="faq">{t('faqLink')}</Link>
              </Typography>
            </li>
            {/*  <li>
              <Typography sx={{ mb: 2 }}>
                <Link href="/" target="_blank">
                  {t('storiesLink')}
                </Link>
              </Typography>
            </li> */}
          </ul>
        </ContentSection>
      </ContentContainer>
      <BrandingDAS
        button={
          <Button
            className="about__getStartedButton"
            color="primary"
            variant="contained"
            endIcon={<Icon path={mdiChevronRight} size={1} />}
            href={t('common:hrefMsFormsGetStarted')}
            target="_blank"
          >
            {t('common:getStarted')}
          </Button>
        }
      />
    </Root>
  );
}
